/**
 * A hero card in the editorial layout: the still fills the card, the product
 * name sits large and low over it, and a bar along the bottom carries one
 * sentence and the link through to the product.
 *
 * The name and the copy are real text laid over the image, not baked into it.
 * That keeps them crisp at any card size, searchable, translatable, and
 * changeable without regenerating anything — and it is the only way the
 * whole catalogue can share one typeface.
 */
import PropTypes from 'prop-types';
import { heroSrcSet, HERO_SIZES } from '@/utils/imageSrcSet';
import { Link } from 'react-router-dom';
import { faceForProduct, heroCardFor } from '@/data/heroCards';
import '@/styles/components/HeroProductCard.css';

const HeroProductCard = ({ product, priority, onNavigate }) => {
  const card = heroCardFor(product);
  const face = faceForProduct(product);
  const art = card?.art || product?.thumbnail;
  const blurb = card?.blurb || product?.shortDescription || '';
  const to = `/product/${product?.slug || product?.id}`;

  /* Store tags like "[FREE]" belong to the catalogue, not to the product's
     name — set large on a card they read as shouting, and they push the real
     name down a size. The tag becomes its own small marker above the name,
     and a card may also override the displayed name outright where the
     catalogue title carries its own explanation. */
  const rawTitle = product?.title || '';
  const free = /\[\s*free\s*\]/i.test(rawTitle);
  const title = card?.title
    || rawTitle.replace(/\[[^\]]*\]/g, ' ').replace(/\s+/g, ' ').trim();

  /* The editorial treatment only applies to products whose still was shot for
     it. The catalogue thumbnails already have the product name burned into
     the artwork, so laying our own name over one of those prints the title
     twice. Until a product has its own still it keeps the plain card, and the
     set fills in one product at a time without the site ever looking broken. */
  const editorial = Boolean(card?.art);

  return (
    <div
      className="herocard"
      /* Where the copy sits, measured per image rather than fixed. The stills
         are real screenshots and each has its empty space somewhere else, so
         a block pinned to the same corner lands on the busiest part of half
         the catalogue and leaves the other half looking hollow. */
      data-align={card?.align || 'left'}
      data-band={card?.band || 'bottom'}
      /* The category, so the grade can differ where the subject demands it.
         A ped is a person: if the face and the clothing are not readable the
         card is showing nothing. A script screenshot is a UI panel and can
         take a much deeper grade without losing what it is. */
      data-kind={(product?.category || '').toLowerCase()}
      /* Catalogue art is finished key art with its own title and grade. The
         editorial grade and veil exist for raw screenshots; over key art they
         only wash it out, so the stylesheet leaves this art as it was made. */
      data-art={editorial ? 'editorial' : 'catalogue'}
      style={{
        '--name-family': `'${face.family}', 'Haettenschweiler', 'Arial Narrow', sans-serif`,
        '--name-weight': face.weight,
        /* A number the stylesheet gives the cqw unit to, so the name stays
           tied to the card rather than to the viewport. */
        '--name-size': face.size,
        '--name-tracking': face.tracking,
      }}
    >
      {art
        ? (
          <img
            className="herocard__art"
            src={art}
            srcSet={heroSrcSet(art)}
            sizes={HERO_SIZES}
            alt=""
            draggable={false}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
          />
        )
        : <div className="herocard__art herocard__art--empty" aria-hidden="true" />}

      {/* Darkens the top edge and the lower third only, so the picture stays a
          picture in the middle while the type above and below stays legible
          whatever the still happens to do there. */}
      <div className="herocard__veil" aria-hidden="true" />

      {editorial ? (
        <>
          <span className="herocard__tag herocard__tag--left">Retro Store</span>
          <span className="herocard__tag herocard__tag--right">{product?.category}</span>
          {/* Badge and name share one block so the badge always sits directly
              on top of the name, whether the name runs to one line or three. */}
          <div className="herocard__head">
            {free ? <span className="herocard__free">Free</span> : null}
            <h2 className="herocard__name">{title}</h2>
          </div>
        </>
      ) : (
        /* Still on catalogue art: the name is already in the picture, so this
           one only needs the category and the way through to the product. */
        <span className="herocard__tag herocard__tag--right">{product?.category}</span>
      )}

      <div className="herocard__bar">
        {editorial && blurb ? <p className="herocard__blurb">{blurb}</p> : <span />}

        {/* A real link, not an image of one. onNavigate lets the carousel
            swallow the click that ends a drag — without it, every swipe that
            happens to finish over the button would navigate away. */}
        <Link
          className="herocard__cta"
          to={to}
          onClick={(event) => onNavigate?.(event, to)}
          draggable={false}
        >
          <span className="herocard__cta-label">View Product</span>
          <span className="herocard__cta-arrow" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
              <path d="M3 8h9M8.5 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </Link>
      </div>
    </div>
  );
};

HeroProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slug: PropTypes.string,
    title: PropTypes.string,
    category: PropTypes.string,
    thumbnail: PropTypes.string,
    shortDescription: PropTypes.string,
  }).isRequired,
  priority: PropTypes.bool,
  onNavigate: PropTypes.func,
};

HeroProductCard.defaultProps = { priority: false, onNavigate: undefined };

export default HeroProductCard;
